import React, { useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import type { MapMarker, MapCenter } from './types';
import { calculateCenter } from './utils';

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

type MapViewportUpdaterProps = {
  center: MapCenter;
};

const MapViewportUpdater: React.FC<MapViewportUpdaterProps> = ({ center }) => {
  const map = useMap();

  useEffect(() => {
    if (
      center.lat != null &&
      center.lng != null &&
      !isNaN(center.lat) &&
      !isNaN(center.lng)
    ) {
      map.setView([center.lat, center.lng], center.zoom || map.getZoom());
    }
  }, [center, map]);

  return null;
};

type OpenStreetMapProps = {
  markers: MapMarker[];
  center?: MapCenter;
  zoom?: number;
  className?: string;
  height?: string;
};

export const OpenStreetMap: React.FC<OpenStreetMapProps> = ({
  markers,
  center: providedCenter,
  zoom = 2,
  className = '',
  height = '400px',
}) => {
  const calculatedCenter = useMemo(() => {
    if (providedCenter) return providedCenter;
    return calculateCenter(markers) || { lat: 0, lng: 0, zoom: 2 };
  }, [markers, providedCenter]);

  const mapCenter = useMemo(() => {
    return {
      lat: calculatedCenter.lat || 0,
      lng: calculatedCenter.lng || 0,
      zoom: calculatedCenter.zoom || zoom,
    };
  }, [calculatedCenter, zoom]);

  if (
    (!mapCenter.lat && mapCenter.lat !== 0) ||
    (!mapCenter.lng && mapCenter.lng !== 0)
  ) {
    return (
      <div
        className={`flex items-center justify-center bg-gray-100 rounded-lg ${className}`}
        style={{ height }}
      >
        <span className="text-secondary">No valid location data available</span>
      </div>
    );
  }

  return (
    <div
      className={`rounded-lg overflow-hidden ${className}`}
      style={{ height }}
    >
      <MapContainer
        center={[mapCenter.lat, mapCenter.lng]}
        zoom={mapCenter.zoom}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={true}
        maxBounds={[
          [-90, -180],
          [90, 180],
        ]}
        maxBoundsViscosity={1.0}
        minZoom={2}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          noWrap={true}
        />
        <MapViewportUpdater center={mapCenter} />
        {markers.map(marker => (
          <Marker
            key={marker.id}
            position={[marker.latitude, marker.longitude]}
          >
            {marker.title && (
              <Popup maxWidth={500} className="custom-popup">
                <div className="p-3 min-w-[150px] max-w-[500px]">
                  {marker.title && (
                    <div className="font-medium mb-2 text-base break-words">
                      {marker.title}
                    </div>
                  )}
                  {marker.description && (
                    <div className="text-secondary whitespace-pre-line text-xxs break-words">
                      {marker.description}
                    </div>
                  )}
                </div>
              </Popup>
            )}
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
};
